import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatCurrency, formatDate, formatDateTime } from "../lib/api";
import StatusBadge from "../components/StatusBadge";
import { ArrowLeft, Mail, Phone, Calendar, MapPin } from "lucide-react";

const CustomerDetail = () => {
    const { id } = useParams();
    const [data, setData] = useState(null);

    useEffect(() => {
        api.get(`/customers/${id}`).then((r) => setData(r.data));
    }, [id]);

    if (!data) return <div className="text-slate-500">Loading…</div>;
    const { customer, rides } = data;

    return (
        <div className="space-y-6">
            <Link to="/customers" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-mint-600" data-testid="customer-back-link">
                <ArrowLeft className="w-4 h-4" /> Back to customers
            </Link>

            <div className="bg-white border border-slate-200 rounded-2xl p-6">
                <div className="flex flex-col md:flex-row gap-6 items-start">
                    <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-mint-400 to-mint-600 text-white flex items-center justify-center font-display font-bold text-3xl">
                        {customer.name?.[0] || "C"}
                    </div>
                    <div className="flex-1">
                        <h1 className="font-display font-bold text-2xl text-navy-900" data-testid="customer-name">{customer.name}</h1>
                        <div className="text-slate-500 mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm">
                            <span className="flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" />{customer.phone}</span>
                            <span className="flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" />{customer.email}</span>
                            <span className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" />{customer.city}</span>
                            <span className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" />Joined {formatDate(customer.created_at)}</span>
                        </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4 w-full md:w-auto">
                        <div className="text-center">
                            <div className="text-xs text-slate-500 uppercase tracking-wider">Rides</div>
                            <div className="font-display font-bold text-navy-900 text-2xl">{customer.total_rides || 0}</div>
                        </div>
                        <div className="text-center">
                            <div className="text-xs text-slate-500 uppercase tracking-wider">Spend</div>
                            <div className="font-display font-bold text-navy-900 text-2xl">{formatCurrency(customer.total_spend || 0)}</div>
                        </div>
                        <div className="text-center">
                            <div className="text-xs text-slate-500 uppercase tracking-wider">Rating</div>
                            <div className="font-display font-bold text-navy-900 text-2xl">{customer.rating} ★</div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
                <div className="px-5 py-4 border-b border-slate-100 font-display font-semibold text-navy-900">Recent rides</div>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 border-b border-slate-200">
                            <tr className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                                <th className="px-4 py-3">Ride ID</th>
                                <th className="px-4 py-3">Driver</th>
                                <th className="px-4 py-3">Route</th>
                                <th className="px-4 py-3 text-right">Fare</th>
                                <th className="px-4 py-3">Status</th>
                                <th className="px-4 py-3">Date</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {rides.length === 0 ? (
                                <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400">No rides yet.</td></tr>
                            ) : rides.map((r) => (
                                <tr key={r.id} className="hover:bg-slate-50">
                                    <td className="px-4 py-3"><Link to={`/rides/${r.id}`} className="text-mint-600 font-semibold hover:underline">{r.id}</Link></td>
                                    <td className="px-4 py-3">{r.driver?.name || "—"}</td>
                                    <td className="px-4 py-3 text-slate-600 max-w-[300px] truncate">{r.pickup_address} → {r.drop_address}</td>
                                    <td className="px-4 py-3 text-right font-semibold">{formatCurrency(r.fare)}</td>
                                    <td className="px-4 py-3"><StatusBadge status={r.status} /></td>
                                    <td className="px-4 py-3 text-slate-500">{formatDateTime(r.created_at)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default CustomerDetail;
